import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DropdownItem, RowSelectionState } from 'design-system-web';
import { bulkAssignMemberMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  assignableUsers: AdminUser[];
  refetch?: () => void;
};

export function BulkAssigneeAction({
  rowSelection,
  assignableUsers,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const [bulkAssignMember] = useMutation(bulkAssignMemberMutation);

  const handleMemberAssign = async (userValue: string) => {
    if (!userValue || !rowSelection) return;

    const selectedWorkOrderIds = Object.entries(rowSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);

    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.noSelection')
      );
      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );
      return;
    }

    const selectedUserObj = assignableUsers.find(
      (u: AdminUser) => u.value === userValue
    );
    const userName = selectedUserObj?.label || userValue;

    try {
      await window.Alert.confirm({
        title: t(
          'web.admin.serviceRequest.bulkActions.assignMember.confirm.title'
        ),
        message: t(
          'web.admin.serviceRequest.bulkActions.assignMember.confirm.message',
          {
            count: selectedWorkOrderIds.length,
            assignee: userName,
          }
        ),
        confirmText: t(
          'web.admin.serviceRequest.bulkActions.assignMember.confirm.button'
        ),
        cancelText: t(
          'web.admin.serviceRequest.bulkActions.assignMember.cancel.button'
        ),
      });
    } catch {
      // User cancelled - no state to clean up
      return;
    }

    window.Alert.loading({
      title: t('web.admin.serviceRequest.bulkActions.assignMember.inProgress'),
      disableCloseOnBackgroundClick: true,
    });

    try {
      const result = await bulkAssignMember({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          assignee: userValue,
        },
      });

      const { successCount, failureCount } = result.data?.bulkAssignMember || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      window.Alert.hide();

      if (failureCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.success', {
            count: successCount,
          })
        );
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignMember.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      window.Alert.hide();
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignMember.error')
      );
    }
  };

  return (
    <div style={{ minWidth: '200px' }}>
      <Dropdown
        isSearchable
        value=""
        items={assignableUsers || []}
        onChange={(item: DropdownItem<string>) => {
          if (item.value) {
            handleMemberAssign(item.value);
          }
        }}
        placeholder={t(
          'web.admin.serviceRequest.bulkActions.assignMember.selectMember'
        )}
        doTranslation={false}
        testId="bulk-assignee-select"
      />
    </div>
  );
}
